import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { axiosInstance } from '../../../helpers'
import { AxiosResponse } from 'axios'
import { CompanyWithInvoiceCode } from '../Property/types'
import { CompanyStatus } from '../../../models'

/** Full property list for Copy Configs, admin user dropdowns, etc. */
export const useProperties = () => {
    const queryClient = useQueryClient()
    const queryKey = ['admin-properties']
    const { data: propertyList, isLoading, isError, error, refetch } = useQuery<
        AxiosResponse<CompanyWithInvoiceCode[]>,
        Error
    >({
        queryKey,
        queryFn: () => axiosInstance.get('/account-manager/properties/'),
    })

    const updatePropertyBilling = async (req: UpdatePropertyRequest) => {
        const url = `/account-manager/properties/${req.params.id}/update-billing-info/`
        const response = await axiosInstance.post(url, req.body)
        const apartment: CompanyWithInvoiceCode = response.data

        queryClient.setQueryData(
            queryKey,
            (old: AxiosResponse<CompanyWithInvoiceCode[]> | undefined) => {
                if (!old || !old.data || !Array.isArray(old.data)) return old
                return {
                    ...old,
                    data: old.data.map((property) =>
                        property.id === req.params.id ? apartment : property,
                    ),
                }
            },
        )

        return apartment
    }

    const updatePropertyStatusMutation = useMutation({
        mutationFn: async ({
            id,
            status,
        }: {
            id: number
            status: CompanyStatus
        }) => {
            const response = await axiosInstance.patch(
                `/account-manager/properties/${id}/`,
                { status },
            )
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey })
        },
    })

    const updatePropertyStatus = async (id: number, status: CompanyStatus) => {
        return updatePropertyStatusMutation.mutateAsync({ id, status })
    }

    return {
        propertyList,
        isLoading,
        isError,
        error,
        refetch,
        updatePropertyBilling,
        updatePropertyStatus,
    }
}

export interface UpdatePropertyRequest {
    params: {
        id: number
    }
    body: {
        billable_unit_count?: number
        billable_unit_rate?: number
    }
}
